import { useState, useEffect, useRef } from 'react';
import { AuthService } from '../services/AuthService';
import { toastError } from '../utils/toast';

interface SessionTimerConfig {
  inactivityTimeoutMs?: number;
  visibleTimerSeconds?: number;
}

export const useSessionTimer = (config: SessionTimerConfig = {}) => {
  const {
    inactivityTimeoutMs = 15 * 60 * 1000, // 15 minutos
    visibleTimerSeconds = 15
  } = config;

  const [timeRemaining, setTimeRemaining] = useState(visibleTimerSeconds);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const visibleTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Check login status
  useEffect(() => {
    const checkLoginStatus = () => {
      const loginStatus = localStorage.getItem('login');
      const newIsLoggedIn = loginStatus === 'true';
      setIsLoggedIn(newIsLoggedIn);
      
      if (newIsLoggedIn) {
        resetInactivityTimer();
      } else {
        stopAllTimers();
      }
    };

    checkLoginStatus();
    
    // Listen for storage changes
    window.addEventListener('storage', checkLoginStatus);
    
    return () => {
      window.removeEventListener('storage', checkLoginStatus);
      stopAllTimers();
    };
  }, []);

  const stopAllTimers = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (visibleTimerRef.current) {
      clearInterval(visibleTimerRef.current);
      visibleTimerRef.current = null;
    }
    setIsVisible(false);
    setTimeRemaining(visibleTimerSeconds);
  };

  const resetInactivityTimer = () => {
    if (!isLoggedIn) return;

    // Clear existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // Start new inactivity timer
    inactivityTimerRef.current = setTimeout(() => {
      // Show visible countdown
      setIsVisible(true);
      setTimeRemaining(visibleTimerSeconds);
      
      // Start visible countdown
      visibleTimerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            // Time's up - logout
            handleSessionExpired();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, inactivityTimeoutMs);
  };

  const handleSessionExpired = () => {
    stopAllTimers();
    // Execute logout asynchronously without blocking
    AuthService.logout().catch(error => {
      console.error('Erro durante logout automático:', error);
    });
    
    // Show notification using toast util
    toastError({ 
      title: 'Aviso', 
      message: 'Sua sessão expirou. Você foi desconectado.', 
      position: 'center', 
      class: 'izi-center-force error-style', 
      timeout: 10000 
    });
  };

  const resetTimer = () => {
    if (!isLoggedIn) return;
    
    // Hide visible timer if showing
    if (isVisible) {
      setIsVisible(false);
      if (visibleTimerRef.current) {
        clearInterval(visibleTimerRef.current);
        visibleTimerRef.current = null;
      }
    }
    
    // Reset inactivity timer
    resetInactivityTimer();
  };

  // Add event listeners for user activity
  useEffect(() => {
    if (!isLoggedIn) return;

    const events = ['click', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    
    events.forEach(event => {
      document.addEventListener(event, resetTimer);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetTimer);
      });
    };
  }, [isLoggedIn]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
    const formattedSeconds = remainingSeconds < 10 ? `0${remainingSeconds}` : remainingSeconds;
    return `${formattedMinutes}:${formattedSeconds}`;
  };

  return {
    timeRemaining,
    isVisible,
    isLoggedIn,
    formatTime,
    resetTimer,
    formattedTime: formatTime(timeRemaining)
  };
};